import { ChangeEvent, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Branch } from "../types/user.type";
import { Vehicle } from "../types/vehicle.type";
import { getAllBranch, getVehicles } from "../services";
import { getBranchCode } from "../services/keycloak";
import { featureAccess } from "../permissions/access";
import { useUserRole } from "../utils/hooks/useUserRole";
import VehicleTable from "../components/Vehicle/VehicleTable";
import VehicleDrawer from "../components/Vehicle/VehicleDrawer";
import { VEHICLE_BRANCH_QUERY_KEY } from "../components/Vehicle/vehiclePaths";
import Button from "@mui/material/Button";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";

const BRANCH_PICKER_MIN_WIDTH = 280;

/** The branch asked for in the link, else the caller's own branch, else the first one listed. */
const pickInitialBranch = (
  branches: Branch[],
  requestedUuid: string | null,
  ownBranchCode: string | null,
): Branch | undefined =>
  branches.find((branch) => branch.uuid === requestedUuid) ??
  branches.find((branch) => branch.branch_code === ownBranchCode) ??
  branches[0];

const formatBranch = (branch: Branch) =>
  `${branch.branch_code} ${branch.branch_name}`;

export default function VehiclePage() {
  const role = useUserRole();
  // MASTER / ADMIN pick the branch; a branch manager only ever sees and adds to their own.
  const canChooseBranch = featureAccess.vehicleAllBranches(role);
  const [searchParams] = useSearchParams();
  const requestedBranchUuid = searchParams.get(VEHICLE_BRANCH_QUERY_KEY);

  const [branches, setBranches] = useState<Branch[]>([]);
  const [selectedBranchUuid, setSelectedBranchUuid] = useState<string>("");
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [reloadCount, setReloadCount] = useState<number>(0);
  const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
  const [editingVehicle, setEditingVehicle] = useState<Vehicle | null>(null);

  useEffect(() => {
    if (!canChooseBranch) return;
    const loadBranches = async () => {
      try {
        const {
          data: { data },
        } = await getAllBranch();
        const branchList: Branch[] = data;
        setBranches(branchList);
        const initialBranch = pickInitialBranch(
          branchList,
          requestedBranchUuid,
          getBranchCode(),
        );
        setSelectedBranchUuid(initialBranch?.uuid ?? "");
      } catch {
        // The axios interceptor reports the failure; the picker stays empty.
      }
    };
    loadBranches();
  }, [canChooseBranch, requestedBranchUuid]);

  const isWaitingForBranch = canChooseBranch && !selectedBranchUuid;

  useEffect(() => {
    if (isWaitingForBranch) return;
    // Switching branches quickly must not let an older answer replace the newer branch's list.
    let ignored = false;
    const loadVehicles = async () => {
      setLoading(true);
      try {
        const {
          data: { data },
        } = await getVehicles(canChooseBranch ? selectedBranchUuid : undefined);
        if (!ignored) setVehicles(data);
      } catch {
        // Reported by the axios interceptor; an empty table never shows another branch's vehicles.
        if (!ignored) setVehicles([]);
      } finally {
        if (!ignored) setLoading(false);
      }
    };
    loadVehicles();
    return () => {
      ignored = true;
    };
  }, [canChooseBranch, isWaitingForBranch, selectedBranchUuid, reloadCount]);

  const targetBranchLabel = useMemo(() => {
    if (!canChooseBranch) return getBranchCode() ?? "";
    const selectedBranch = branches.find(
      (branch) => branch.uuid === selectedBranchUuid,
    );
    return selectedBranch ? formatBranch(selectedBranch) : "";
  }, [canChooseBranch, branches, selectedBranchUuid]);

  const handleChangeBranch = (event: ChangeEvent<HTMLInputElement>) => {
    setSelectedBranchUuid(event.target.value);
  };

  const handleOpenCreate = () => {
    setEditingVehicle(null);
    setDrawerOpen(true);
  };

  const handleOpenEdit = (vehicle: Vehicle) => {
    setEditingVehicle(vehicle);
    setDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setEditingVehicle(null);
  };

  const handleSaved = () => setReloadCount((count) => count + 1);

  return (
    <Stack gap={2} sx={{ py: 1 }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        gap={2}
      >
        <Typography variant="h6">รถ</Typography>
        <Stack direction="row" alignItems="center" gap={2} flexWrap="wrap">
          {canChooseBranch && (
            <TextField
              select
              size="small"
              label="สาขา"
              value={selectedBranchUuid}
              onChange={handleChangeBranch}
              sx={{ minWidth: BRANCH_PICKER_MIN_WIDTH }}
            >
              {branches.map((branch) => (
                <MenuItem key={branch.uuid} value={branch.uuid}>
                  {formatBranch(branch)}
                </MenuItem>
              ))}
            </TextField>
          )}
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            disabled={isWaitingForBranch}
            onClick={handleOpenCreate}
          >
            เพิ่มรถ
          </Button>
        </Stack>
      </Stack>

      <VehicleTable
        vehicles={vehicles}
        loading={loading || isWaitingForBranch}
        onEdit={handleOpenEdit}
      />

      <VehicleDrawer
        open={drawerOpen}
        vehicle={editingVehicle}
        targetBranchUuid={canChooseBranch ? selectedBranchUuid : undefined}
        targetBranchLabel={targetBranchLabel}
        onClose={handleCloseDrawer}
        onSaved={handleSaved}
      />
    </Stack>
  );
}
